import React from 'react';
import type { DriveDetailLevel, DriveLayoutMode, DriveWindowMode } from './types';
import { driveDetailLevelLabel } from './driveStateColors';
import { formatReportingPeriodLabel } from './driveWindow';
import styles from '../ControlBridge.module.css';

export interface PersonalLayerDriveControlsProps {
  lod?: DriveDetailLevel;
  /** Artifact-flow board: hide legacy Flow / Grandchildren toggles */
  artifactFlowMode?: boolean;
  /** Envelope Drive: Window / period only (no Detail or Projected). */
  windowOnly?: boolean;
  /** Inline: single-row toolbar for compact Drive header */
  variant?: 'stack' | 'inline';
  layout?: DriveLayoutMode;
  projected?: boolean;
  showGrandchildren?: boolean;
  win: DriveWindowMode;
  periodDiv: 'month' | 'quarter' | 'year';
  periodOff: number;
  dStart: string;
  dEnd: string;
  futureWindowHint?: string;
  onLodChange?: (lod: DriveDetailLevel) => void;
  onLayoutChange?: (layout: DriveLayoutMode) => void;
  onProjectedChange?: (projected: boolean) => void;
  onShowGrandchildrenChange?: (show: boolean) => void;
  onWinChange: (win: DriveWindowMode) => void;
  onPeriodDivChange: (div: 'month' | 'quarter' | 'year') => void;
  onPeriodStep: (delta: number) => void;
  onDateRangeChange: (field: 'dStart' | 'dEnd', value: string) => void;
}

export const PersonalLayerDriveControls: React.FC<PersonalLayerDriveControlsProps> = ({
  lod,
  artifactFlowMode = false,
  windowOnly = false,
  variant = 'stack',
  layout = 'lr',
  projected = false,
  showGrandchildren = false,
  win,
  periodDiv,
  periodOff,
  dStart,
  dEnd,
  futureWindowHint,
  onLodChange,
  onLayoutChange,
  onProjectedChange,
  onShowGrandchildrenChange,
  onWinChange,
  onPeriodDivChange,
  onPeriodStep,
  onDateRangeChange,
}) => {
  const periodLabel = formatReportingPeriodLabel(periodDiv, periodOff);
  const panelClass =
    variant === 'inline' ? styles.driveControlPanelInline : styles.driveControlPanel;
  const stackClass =
    variant === 'inline' ? styles.driveControlStackInline : styles.driveControlStack;

  return (
    <div className={stackClass}>
      <div className={panelClass} role="toolbar" aria-label="Drive board controls">
        {!windowOnly ? (
          <div className={styles.driveControlGroup}>
            <span className={styles.driveControlLabel}>Detail</span>
            {(['summary', 'token', 'card'] as const).map((value) => (
              <button
                key={value}
                type="button"
                className={`${styles.driveControlBtn} ${lod === value ? styles.driveControlBtnActive : ''}`}
                aria-pressed={lod === value}
                onClick={() => onLodChange?.(value)}
              >
                {driveDetailLevelLabel(value)}
              </button>
            ))}
          </div>
        ) : null}
        {!windowOnly && !artifactFlowMode ? (
          <>
            <div className={styles.driveControlGroup}>
              <span className={styles.driveControlLabel}>Flow</span>
              <button
                type="button"
                className={`${styles.driveControlBtn} ${layout === 'lr' ? styles.driveControlBtnActive : ''}`}
                aria-pressed={layout === 'lr'}
                onClick={() => onLayoutChange?.('lr')}
              >
                Left to right
              </button>
              <button
                type="button"
                className={`${styles.driveControlBtn} ${layout === 'compact' ? styles.driveControlBtnActive : ''}`}
                aria-pressed={layout === 'compact'}
                onClick={() => onLayoutChange?.('compact')}
              >
                Compact
              </button>
            </div>
            <div className={styles.driveControlGroup}>
              <span className={styles.driveControlLabel}>Grandchildren</span>
              <button
                type="button"
                className={`${styles.driveControlBtn} ${showGrandchildren ? styles.driveControlBtnActive : ''}`}
                aria-pressed={showGrandchildren}
                onClick={() => onShowGrandchildrenChange?.(!showGrandchildren)}
              >
                {showGrandchildren ? 'On' : 'Off'}
              </button>
            </div>
          </>
        ) : null}
        {!windowOnly ? (
          <div className={styles.driveControlGroup}>
            <span className={styles.driveControlLabel}>Projected</span>
            <button
              type="button"
              className={`${styles.driveControlBtn} ${projected ? styles.driveControlBtnActive : ''}`}
              aria-pressed={projected}
              onClick={() => onProjectedChange?.(!projected)}
            >
              {projected ? 'On' : 'Off'}
            </button>
          </div>
        ) : null}
        <div className={styles.driveControlGroup}>
          <span className={styles.driveControlLabel}>Window</span>
          {(
            [
              ['today', 'Today'],
              ['period', 'Reporting period'],
              ['daterange', 'Date range'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`${styles.driveControlBtn} ${win === value ? styles.driveControlBtnActive : ''}`}
              aria-pressed={win === value}
              onClick={() => onWinChange(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {win === 'period' ? (
        <div className={styles.driveControlSubRow} role="group" aria-label="Reporting period">
          {(['month', 'quarter', 'year'] as const).map((div) => (
            <button
              key={div}
              type="button"
              className={`${styles.driveControlBtn} ${periodDiv === div ? styles.driveControlBtnActive : ''}`}
              aria-pressed={periodDiv === div}
              onClick={() => onPeriodDivChange(div)}
            >
              {div.charAt(0).toUpperCase() + div.slice(1)}
            </button>
          ))}
          <button
            type="button"
            className={styles.driveControlBtn}
            aria-label="Previous period"
            onClick={() => onPeriodStep(-1)}
          >
            ‹
          </button>
          <span className={styles.drivePeriodLabel}>{periodLabel}</span>
          <button
            type="button"
            className={styles.driveControlBtn}
            aria-label="Next period"
            onClick={() => onPeriodStep(1)}
          >
            ›
          </button>
        </div>
      ) : null}

      {win === 'daterange' ? (
        <div className={styles.driveControlSubRow} role="group" aria-label="Date range">
          <label className={styles.driveDateLabel}>
            <span className={styles.driveControlLabel}>From</span>
            <input
              type="date"
              className={styles.driveDateInput}
              value={dStart}
              aria-label="Window range start"
              onChange={(e) => onDateRangeChange('dStart', e.target.value)}
            />
          </label>
          <label className={styles.driveDateLabel}>
            <span className={styles.driveControlLabel}>To</span>
            <input
              type="date"
              className={styles.driveDateInput}
              value={dEnd}
              aria-label="Window range end"
              onChange={(e) => onDateRangeChange('dEnd', e.target.value)}
            />
          </label>
        </div>
      ) : null}

      {futureWindowHint ? (
        <p className={styles.driveWindowHint} role="status">
          {futureWindowHint}
        </p>
      ) : null}
    </div>
  );
};
